// src/platform/back-handler.ts
// Stack-based Android back button handler.
// Overlays register a close callback when they open, and unregister when they close.
// The back button pops the most recently registered handler.

type BackHandler = () => void;

const stack: { id: string; handler: BackHandler }[] = [];

/**
 * Register a back handler for an overlay. Call the returned function to unregister.
 * @param id Unique identifier for the overlay (e.g. 'sidebar', 'settings')
 * @param handler Function to call when back is pressed (should close the overlay)
 */
export function pushBackHandler(id: string, handler: BackHandler): () => void {
  // Remove existing entry with same id to avoid duplicates
  const idx = stack.findIndex((e) => e.id === id);
  if (idx !== -1) stack.splice(idx, 1);

  stack.push({ id, handler });
  return () => {
    const i = stack.findIndex((e) => e.id === id);
    if (i !== -1) stack.splice(i, 1);
  };
}

/**
 * Remove a back handler by id (for use in cleanup effects).
 */
export function removeBackHandler(id: string): void {
  const idx = stack.findIndex((e) => e.id === id);
  if (idx !== -1) stack.splice(idx, 1);
}

/**
 * Pop and execute the top handler. Returns true if a handler was found.
 */
export function popBackHandler(): boolean {
  if (stack.length === 0) return false;
  const entry = stack.pop()!;
  entry.handler();
  return true;
}

/**
 * Check if there are any registered handlers.
 */
export function hasBackHandlers(): boolean {
  return stack.length > 0;
}
